import React from 'react';
import { Play } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The things people save, drawn as flat canvas cards for the landing page.
 * Pictures are flat SVG shapes in sticky and folder tones: no stock photos,
 * no gradients. Every card is the size it is on the canvas at 100% zoom.
 */
export type CardKind =
  | 'sticky'
  | 'chair'
  | 'link'
  | 'voice'
  | 'quote'
  | 'palette'
  | 'fjord'
  | 'todo'
  | 'pdf';

export const CARD_SIZE: Record<CardKind, { w: number; h: number }> = {
  sticky: { w: 168, h: 168 },
  chair: { w: 164, h: 208 },
  link: { w: 236, h: 196 },
  voice: { w: 228, h: 68 },
  quote: { w: 208, h: 150 },
  palette: { w: 196, h: 118 },
  fjord: { w: 232, h: 156 },
  todo: { w: 188, h: 146 },
  pdf: { w: 148, h: 192 },
};

const shell = 'h-full w-full overflow-hidden rounded-[14px]';
const raised = 'bg-paper-raised border border-line shadow-sm';
const meta = 'font-mono text-[11px] leading-4 text-ink-3';

function Sticky() {
  return (
    <div className={cn(shell, 'flex flex-col justify-between bg-[hsl(var(--sticky-yellow))] p-4 shadow-sm')}>
      <p className="text-[15px] font-medium leading-5 text-[hsl(var(--sticky-text))]">
        Ask Lena where the kitchen tiles are from
      </p>
      <span className="font-mono text-[11px] text-[hsl(var(--sticky-text))]/60">Tue</span>
    </div>
  );
}

/** A chair on a sky wall: the thing Partnr finds in section two. */
export function ChairArt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 164 208" className={className} preserveAspectRatio="xMidYMid slice" aria-hidden>
      <rect width="164" height="208" fill="hsl(209 47% 88.8%)" />
      <rect y="150" width="164" height="58" fill="hsl(45 20% 82%)" />
      <ellipse cx="84" cy="176" rx="46" ry="5" fill="hsl(45 14% 70%)" />
      {/* Back and seat in birch, legs in cobalt */}
      <path d="M58 58 h48 a6 6 0 0 1 6 6 v44 h-60 v-44 a6 6 0 0 1 6 -6Z" fill="hsl(36 46% 74%)" />
      <rect x="46" y="104" width="76" height="14" rx="5" fill="hsl(36 40% 66%)" />
      <path d="M56 118 L48 176 M112 118 L120 176 M68 118 L66 168 M100 118 L102 168" stroke="#2E54A0" strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

function Chair() {
  return (
    <div className={cn(shell, 'shadow-sm')}>
      <ChairArt className="h-full w-full" />
    </div>
  );
}

function Link() {
  return (
    <div className={cn(shell, raised, 'flex flex-col')}>
      <svg viewBox="0 0 236 112" className="h-[112px] w-full shrink-0" aria-hidden>
        <rect width="236" height="112" fill="hsl(7 46% 89%)" />
        <circle cx="168" cy="44" r="22" fill="hsl(12 42% 76%)" />
        <rect x="0" y="78" width="236" height="34" fill="hsl(12 34% 64%)" />
        <rect x="28" y="56" width="58" height="30" rx="4" fill="hsl(45 20% 94%)" />
      </svg>
      <div className="flex flex-1 flex-col justify-between p-3">
        <p className="text-[13px] font-medium leading-[18px] text-ink">A field guide to slow mornings</p>
        <span className={meta}>slowmornings.blog</span>
      </div>
    </div>
  );
}

const BARS = [6, 12, 20, 14, 26, 18, 10, 22, 30, 16, 8, 18, 24, 12, 20, 9, 15, 6];

function Voice() {
  return (
    <div className={cn(shell, raised, 'flex items-center gap-3 rounded-full px-2.5')}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-paper-raised">
        <Play className="ml-0.5 h-4 w-4 fill-current" />
      </span>
      <div className="flex h-8 flex-1 items-center gap-[3px]" aria-hidden>
        {BARS.map((h, i) => (
          <span key={i} className="w-[3px] rounded-full bg-ink/70" style={{ height: h }} />
        ))}
      </div>
      <span className={cn(meta, 'pr-2')}>0:42</span>
    </div>
  );
}

function Quote() {
  return (
    <div className={cn(shell, raised, 'flex flex-col justify-between p-4')}>
      <p className="font-display text-[19px] font-medium leading-[24px] tracking-[-0.01em] text-ink">
        “Write it down. Then let yourself forget it.”
      </p>
      <span className={meta}>From a podcast, 41:08</span>
    </div>
  );
}

const SWATCHES = [
  { hsl: '49 72.7% 82.7%', hex: 'F3E7B3' },
  { hsl: '209 47.4% 88.8%', hex: 'D5E3F0' },
  { hsl: '7 46.4% 89%', hex: 'F0D9D6' },
  { hsl: '130 18% 72%', hex: 'ADC6B1' },
];

function Palette() {
  return (
    <div className={cn(shell, raised, 'flex flex-col p-2')}>
      <div className="flex flex-1 gap-1">
        {SWATCHES.map((s) => (
          <span key={s.hex} className="flex-1 rounded-[8px]" style={{ background: `hsl(${s.hsl})` }} />
        ))}
      </div>
      <div className="flex justify-between px-1 pt-2">
        <span className={meta}>Bedroom</span>
        <span className={meta}>4 colors</span>
      </div>
    </div>
  );
}

/** A flat fjord: sun, two ridges, water. */
function Fjord() {
  return (
    <div className={cn(shell, 'shadow-sm')}>
      <svg viewBox="0 0 232 156" className="h-full w-full" aria-hidden>
        <rect width="232" height="156" fill="hsl(209 47% 88.8%)" />
        <circle cx="160" cy="52" r="18" fill="hsl(49 72.7% 82.7%)" />
        <path d="M0 104 L44 58 L78 86 L118 40 L170 98 L232 70 V156 H0Z" fill="hsl(225 26% 64%)" />
        <path d="M0 116 L60 82 L104 108 L150 76 L232 112 V156 H0Z" fill="hsl(130 15% 60%)" />
        <rect y="122" width="232" height="34" fill="hsl(225 32% 76%)" />
        <path d="M24 134 h40 M96 142 h56 M170 132 h36" stroke="hsl(209 47% 92%)" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function Todo() {
  const rows = [
    { t: 'Return library books', done: true },
    { t: 'Fix the bike light', done: false },
    { t: 'Book the dentist', done: false },
  ];
  return (
    <div className={cn(shell, raised, 'p-4')}>
      <p className="mb-2.5 font-display text-[15px] font-semibold text-ink">Weekend</p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.t} className="flex items-center gap-2 text-[13px] leading-4">
            <span
              className={cn(
                'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] border',
                r.done ? 'border-ink bg-ink' : 'border-ink/30',
              )}
            >
              {r.done && (
                <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" aria-hidden>
                  <path d="M2 5.2 L4.2 7.2 L8 3" fill="none" stroke="hsl(var(--paper-raised))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className={r.done ? 'text-ink-3 line-through' : 'text-ink-2'}>{r.t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Pdf() {
  return (
    <div className={cn(shell, raised, 'flex flex-col p-3')}>
      <div className="mb-2 h-2 w-3/4 rounded-full bg-ink/80" />
      <div className="mb-3 h-1.5 w-1/2 rounded-full bg-ink/25" />
      {[100, 92, 96, 70, 0, 100, 88, 94, 60].map((w, i) =>
        w === 0 ? (
          <div key={i} className="h-2.5" />
        ) : (
          <div key={i} className="mb-1.5 h-1 rounded-full bg-ink/15" style={{ width: `${w}%` }} />
        ),
      )}
      <span className={cn(meta, 'mt-auto flex justify-between whitespace-nowrap')}>
        <span>brief-v3.pdf</span>
        <span>12 pp</span>
      </span>
    </div>
  );
}

const FACES: Record<CardKind, React.FC> = {
  sticky: Sticky,
  chair: Chair,
  link: Link,
  voice: Voice,
  quote: Quote,
  palette: Palette,
  fjord: Fjord,
  todo: Todo,
  pdf: Pdf,
};

/** Plain-text names, for accessible labels. */
export const CARD_LABEL: Record<CardKind, string> = {
  sticky: 'Note: ask Lena where the kitchen tiles are from',
  chair: 'Photo of a birch chair with blue legs',
  link: 'Link: A field guide to slow mornings',
  voice: 'Voice memo, 42 seconds',
  quote: 'Quote: Write it down. Then let yourself forget it.',
  palette: 'Bedroom color palette',
  fjord: 'Photo of a fjord',
  todo: 'Weekend to-do list',
  pdf: 'PDF: brief-v3, 12 pages',
};

export function CardFace({ kind }: { kind: CardKind }) {
  const Face = FACES[kind];
  const { w, h } = CARD_SIZE[kind];
  return (
    <div style={{ width: w, height: h }} className="select-none">
      <Face />
    </div>
  );
}
